/**
 * `<caos-channel-list>` — the conversations somebody can open, and which of them are waiting. (Catalog: Conversation › ChannelList — the conversations somebody can open, gathered into sections, with what is unread.)
 *
 * The navigation half of a conversation surface: named conversations, optionally gathered into
 * sections, one of them current, and — the whole reason this is not a plain list — an UNREAD state
 * per row.
 *
 * ── Why unread makes this its own piece ──────────────────────────────────────────────────────
 * `<caos-tree>` already draws a hierarchy with one row selected, and for a list of conversations
 * that never changes it would do. Unread is what a conversation list is FOR: the reader's question
 * is "where has something happened since I last looked", and a list that answers it only by making
 * a row bold is a list where the one piece of information anybody came for is a font weight. So an
 * unread row is emphasised AND carries its count, and the count is announced rather than drawn as a
 * decoration, because the difference between one message and forty decides what a person opens.
 *
 * ── What it reports, and what it does not do ─────────────────────────────────────────────────
 * Choosing a row fires `select` and changes nothing: the surface decides what opening a
 * conversation means, and a list that marked itself current before the conversation had opened
 * would be lying whenever opening failed. The same is true of the optional `new` control, which
 * reports and does not create.
 *
 * Searching is done HERE, over the names it was given, because it is narrowing a list already on
 * screen. A search that has to go somewhere is not this control's.
 *
 * Styling seams: `::part(list)`, `::part(search)`, `::part(section)`, `::part(channel)`,
 * `::part(unread)`, `::part(new)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { applyAccessibleName } from '../accessible-name.js';

/** One conversation somebody can open. */
export interface CaosChannel {
  /** Stable identity — what `select` reports and what `current` names. */
  id: string;
  /** What it is called, as it is drawn. */
  label: string;
  /** How many messages are waiting. 0 or absent ⇒ nothing is. */
  unread?: number;
  /** Which section it is gathered under. Absent ⇒ it stands at the top level. */
  section?: string;
}

const sheet = makeSheet(`
  :host { display: flex; flex-direction: column; min-height: 0; font-family: var(--caos-font); }

  .search {
    margin: var(--caos-space-2) var(--caos-space-2) var(--caos-space-1);
    padding: 6px var(--caos-space-2);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text);
    font: inherit;
    font-size: var(--caos-text-row);
  }
  .search:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .list { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding-bottom: var(--caos-space-2); }

  .section {
    padding: var(--caos-space-2) var(--caos-space-3) var(--caos-space-1);
    color: var(--caos-color-text-subtle);
    font-size: var(--caos-text-label);
    font-weight: var(--caos-font-weight-label);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .channel {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    width: 100%;
    appearance: none;
    border: 0;
    background: transparent;
    color: var(--caos-color-text-muted);
    font: inherit;
    font-size: var(--caos-text-row);
    text-align: start;
    padding: 5px var(--caos-space-3);
    cursor: pointer;
  }
  .channel:hover { color: var(--caos-color-text); background: var(--caos-color-surface-2); }
  .channel:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .channel[aria-current="true"] {
    color: var(--caos-color-accent-contrast);
    background: var(--caos-color-accent-fill);
  }
  /* Unread is the thing this list exists to show, so it is the one row state that changes weight. */
  .channel[data-unread="true"] { color: var(--caos-color-text); font-weight: var(--caos-font-weight-label); }

  .name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .count {
    flex: 0 0 auto;
    min-width: 18px;
    text-align: center;
    padding: 1px 6px;
    border-radius: var(--caos-radius-pill);
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    font-size: var(--caos-text-label);
  }
  .channel[aria-current="true"] .count {
    background: var(--caos-color-accent-contrast);
    color: var(--caos-color-accent-fill);
  }

  .new {
    margin: var(--caos-space-1) var(--caos-space-2) var(--caos-space-2);
    appearance: none;
    border: 1px dashed var(--caos-color-border);
    background: transparent;
    color: var(--caos-color-text-muted);
    font: inherit;
    font-size: var(--caos-text-row);
    padding: 6px var(--caos-space-2);
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
  }
  .new:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .empty {
    padding: var(--caos-space-3);
    color: var(--caos-color-text-muted);
    font-size: var(--caos-text-row);
  }
`);

export class CaosChannelList extends CaosElement {
  static get observedAttributes(): string[] {
    return ['current', 'search-placeholder', 'searchable', 'new-label', 'empty-label', 'aria-label'];
  }

  private readonly search: HTMLInputElement;
  private readonly list: HTMLDivElement;
  private readonly newButton: HTMLButtonElement;
  private _channels: CaosChannel[] = [];
  private term = '';

  constructor() {
    super(sheet);
    this.search = document.createElement('input');
    this.search.type = 'search';
    this.search.className = 'search';
    this.search.setAttribute('part', 'search');
    this.search.hidden = true;
    this.search.addEventListener('input', () => {
      this.term = this.search.value.trim().toLowerCase();
      this.renderList();
    });

    this.list = document.createElement('div');
    this.list.className = 'list';
    this.list.setAttribute('part', 'list');
    // A navigation list, not a set of unrelated buttons.
    this.list.setAttribute('role', 'list');

    this.newButton = document.createElement('button');
    this.newButton.type = 'button';
    this.newButton.className = 'new';
    this.newButton.setAttribute('part', 'new');
    this.newButton.hidden = true;
    this.newButton.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('new', { bubbles: true, composed: true }));
    });

    this.root.append(this.search, this.list, this.newButton);
  }

  connectedCallback(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'navigation');
    this.render();
  }

  attributeChangedCallback(name: string): void {
    if (name === 'aria-label') {
      applyAccessibleName(this.list, this.getAttribute('aria-label'), 'Conversations');
      return;
    }
    this.render();
  }

  /** The conversations, in the order they should read. */
  get channels(): CaosChannel[] {
    return this._channels;
  }
  set channels(list: readonly CaosChannel[]) {
    this._channels = [...list];
    this.renderList();
  }

  /** Which one is open, by id. */
  get current(): string {
    return this.getAttribute('current') ?? '';
  }
  set current(value: string) {
    this.setAttribute('current', value);
  }

  private render(): void {
    this.search.hidden = !this.hasAttribute('searchable');
    this.search.placeholder = this.getAttribute('search-placeholder') ?? 'Find a conversation';
    applyAccessibleName(this.search, this.search.placeholder, 'Find a conversation');
    const newLabel = this.getAttribute('new-label');
    this.newButton.hidden = newLabel === null;
    this.newButton.textContent = newLabel ?? '';
    applyAccessibleName(this.list, this.getAttribute('aria-label'), 'Conversations');
    this.renderList();
  }

  private renderList(): void {
    const term = this.term;
    const matching = term === ''
      ? this._channels
      : this._channels.filter((c) => c.label.toLowerCase().includes(term));

    this.list.replaceChildren();
    if (matching.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty';
      empty.setAttribute('part', 'empty');
      // Two different empties, said differently: nothing here at all, versus nothing matching what
      // was typed. A list that says the same for both reads as one that failed to load.
      empty.textContent = term === ''
        ? (this.getAttribute('empty-label') ?? 'No conversations yet.')
        : `Nothing matches “${this.search.value.trim()}”.`;
      this.list.append(empty);
      return;
    }

    let section: string | null = null;
    for (const channel of matching) {
      const belongs = channel.section ?? null;
      if (belongs !== section) {
        section = belongs;
        if (belongs !== null) {
          const heading = document.createElement('div');
          heading.className = 'section';
          heading.setAttribute('part', 'section');
          heading.setAttribute('role', 'presentation');
          heading.textContent = belongs;
          this.list.append(heading);
        }
      }

      const unread = channel.unread ?? 0;
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'channel';
      row.setAttribute('part', unread > 0 ? 'channel unread' : 'channel');
      row.setAttribute('role', 'listitem');
      row.dataset['channelId'] = channel.id;
      row.dataset['unread'] = String(unread > 0);
      if (channel.id === this.current) row.setAttribute('aria-current', 'true');

      const name = document.createElement('span');
      name.className = 'name';
      name.textContent = channel.label;
      row.append(name);

      if (unread > 0) {
        const count = document.createElement('span');
        count.className = 'count';
        count.textContent = String(unread);
        // The count IS the message, so it is named rather than left as a number beside a name.
        // "forty waiting" and "one waiting" lead to different decisions.
        count.setAttribute('aria-label', `${unread} unread`);
        row.append(count);
      }

      row.addEventListener('click', () => {
        this.dispatchEvent(
          new CustomEvent('select', { bubbles: true, composed: true, detail: { id: channel.id } }),
        );
      });
      this.list.append(row);
    }
  }
}

define('caos-channel-list', CaosChannelList);

declare global {
  interface HTMLElementTagNameMap {
    'caos-channel-list': CaosChannelList;
  }
}
